// src/components/EditTransactionModal.js
import React, { useState, useEffect } from 'react';
import { updateTransaction, getTransactionById } from '../services/api';
import { format, parseISO } from 'date-fns';

const EditTransactionModal = ({ transaction, onClose }) => {
    const [formData, setFormData] = useState({
        id: '',
        descricao: '',
        valor: '',
        data: '',
        forma_pagamento: 'Dinheiro',
        tipo: 'entrada',
        cliente: '',
        observacoes: ''
    });

    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    // Efeito para carregar os detalhes da transação quando o modal é aberto
    useEffect(() => {
        const fetchTransactionDetails = async () => {
            try {
                const response = await getTransactionById(transaction.id);
                const data = response.data;

                setFormData({
                    id: data.id,
                    descricao: data.descricao,
                    valor: new Intl.NumberFormat('pt-BR', {
                        style: 'decimal',
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2
                    }).format(data.valor),
                    data: format(parseISO(data.data), 'yyyy-MM-dd'),
                    forma_pagamento: data.forma_pagamento,
                    tipo: data.tipo || 'entrada',
                    cliente: data.cliente || '',
                    observacoes: data.observacoes || ''
                });

                setLoading(false);
            } catch (err) {
                setError('Erro ao carregar dados da transação.');
                console.error("Erro ao carregar transação para edição:", err);
                setLoading(false);
            }
        };

        fetchTransactionDetails();
    }, [transaction.id]);

    // Lida com a mudança de qualquer campo do formulário
    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    // Lida com a formatação do campo de valor em tempo real
    const handleValorChange = (e) => {
        let value = e.target.value.replace(/\D/g, '');
        value = (Number(value) / 100).toLocaleString('pt-BR', {
            style: 'decimal',
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
        });
        setFormData(prev => ({
            ...prev,
            valor: value
        }));
    };

    // Lida com a submissão do formulário
    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError('');

        try {
            const valorNumerico = parseFloat(formData.valor.replace('.', '').replace(',', '.'));

            await updateTransaction(formData.id, {
                ...formData,
                valor: valorNumerico
            });

            onClose();
        } catch (err) {
            setError('Erro ao atualizar transação. Tente novamente.');
            console.error("Erro ao salvar edição da transação:", err);
        } finally {
            setLoading(false);
        }
    };

    // Exibe um estado de carregamento enquanto os dados são buscados
    if (loading) {
        return (
            <div className="modal-overlay">
                <div className="modal-content" style={modalContentStyle}>
                    <p style={{ fontSize: '14px' }}>Carregando dados da transação...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="modal-overlay">
            <div className="modal-content" style={modalContentStyle}> {/* Estilo aplicado aqui */}
                <h2 style={modalTitleStyle}>Editar Transação</h2>

                {error && <p style={{ color: 'red', marginBottom: '10px', fontSize: '13px' }}>{error}</p>}

                <form onSubmit={handleSubmit}>
                    <div className="form-group" style={formGroupStyle}> {/* Estilo aplicado aqui */}
                        <label htmlFor="descricao" style={labelStyle}>Descrição *</label>
                        <input
                            type="text"
                            id="descricao"
                            name="descricao"
                            value={formData.descricao}
                            onChange={handleChange}
                            required
                            style={inputStyle} /* Estilo aplicado aqui */
                        />
                    </div>

                    <div className="form-row" style={formRowStyle}> {/* Estilo aplicado aqui */}
                        <div className="form-group" style={{ flex: 1 }}>
                            <label htmlFor="valor" style={labelStyle}>Valor *</label>
                            <input
                                type="text"
                                id="valor"
                                name="valor"
                                value={formData.valor}
                                onChange={handleValorChange}
                                required
                                style={inputStyle} /* Estilo aplicado aqui */
                            />
                        </div>

                        <div className="form-group" style={{ flex: 1 }}>
                            <label htmlFor="data" style={labelStyle}>Data *</label>
                            <input
                                type="date"
                                id="data"
                                name="data"
                                value={formData.data}
                                onChange={handleChange}
                                required
                                style={inputStyle} /* Estilo aplicado aqui */
                            />
                        </div>
                    </div>

                    {/* Campo para selecionar o tipo de transação (Entrada/Saída) */}
                    <div className="form-group" style={formGroupStyle}> {/* Estilo aplicado aqui */}
                        <label htmlFor="tipo" style={labelStyle}>Tipo de Transação *</label>
                        <select
                            id="tipo"
                            name="tipo"
                            value={formData.tipo}
                            onChange={handleChange}
                            required
                            style={inputStyle} /* Estilo aplicado aqui */
                        >
                            <option value="entrada">Entrada</option>
                            <option value="saida">Saída</option>
                        </select>
                    </div>

                    <div className="form-group" style={formGroupStyle}> {/* Estilo aplicado aqui */}
                        <label htmlFor="forma_pagamento" style={labelStyle}>Forma de Pagamento *</label>
                        <select
                            id="forma_pagamento"
                            name="forma_pagamento"
                            value={formData.forma_pagamento}
                            onChange={handleChange}
                            required
                            style={inputStyle} /* Estilo aplicado aqui */
                        >
                            <option value="Dinheiro">Dinheiro</option>
                            <option value="Cartão de Crédito">Cartão de Crédito</option>
                            <option value="Cartão de Débito">Cartão de Débito</option>
                            <option value="PIX">PIX</option>
                            <option value="Transferência">Transferência</option>
                        </select>
                    </div>

                    <div className="form-group" style={formGroupStyle}> {/* Estilo aplicado aqui */}
                        <label htmlFor="cliente" style={labelStyle}>Cliente (Opcional)</label>
                        <input
                            type="text"
                            id="cliente"
                            name="cliente"
                            value={formData.cliente}
                            onChange={handleChange}
                            style={inputStyle} /* Estilo aplicado aqui */
                        />
                    </div>

                    <div className="form-group" style={formGroupStyle}> {/* Estilo aplicado aqui */}
                        <label htmlFor="observacoes" style={labelStyle}>Observações (Opcional)</label>
                        <textarea
                            id="observacoes"
                            name="observacoes"
                            value={formData.observacoes}
                            onChange={handleChange}
                            rows="3"
                            style={{ ...inputStyle, minHeight: '60px', resize: 'vertical' }} /* Estilo aplicado aqui, ajustado para textarea */
                        />
                    </div>

                    <div style={buttonGroupStyle}> {/* Estilo aplicado aqui */}
                        <button
                            type="button"
                            className="btn btn-danger"
                            onClick={onClose}
                            disabled={loading}
                            style={cancelButtonStyle} /* Estilo aplicado aqui */
                        >
                            Cancelar
                        </button>
                        <button
                            type="submit"
                            className="btn btn-primary"
                            disabled={loading}
                            style={saveButtonStyle} /* Estilo aplicado aqui */
                        >
                            {loading ? 'Salvando...' : 'Salvar'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

// --- ESTILOS COMPACTADOS PARA O MODAL (Compartilhados com AddTransactionModal) ---
const modalContentStyle = {
    padding: '20px',
    borderRadius: '8px',
    width: '100%',
    maxWidth: '450px',
};

const modalTitleStyle = {
    fontSize: '20px',
    marginBottom: '15px'
};

const formGroupStyle = {
    marginBottom: '12px',
};

const formRowStyle = {
    display: 'flex',
    gap: '12px',
    marginBottom: '12px',
};

const labelStyle = {
    display: 'block',
    marginBottom: '5px',
    fontWeight: '600',
    color: '#555',
    fontSize: '13px',
};

const inputStyle = {
    width: '100%',
    padding: '8px 10px',
    border: '1px solid #ddd',
    borderRadius: '4px',
    fontSize: '14px',
};

const buttonGroupStyle = {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: '8px',
    marginTop: '15px',
};

const baseButtonStyle = {
    padding: '8px 15px',
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
    fontWeight: '600',
    fontSize: '14px',
};

const cancelButtonStyle = {
    ...baseButtonStyle,
    backgroundColor: '#e74c3c',
    color: 'white',
};

const saveButtonStyle = {
    ...baseButtonStyle,
    backgroundColor: '#2ecc71',
    color: 'white',
};

export default EditTransactionModal;